import type { FieldOption } from "../../lib/field-util";

// Generic enum/relation select. When `editable` is false, renders the
// selected option's label as plain text (used for read-only profile views).
//
// `variant` — rule of thumb:
//   'box'   - white field with hairline ring. Use when the select stands
//             alone as a hero control in a panel; the ring carries the
//             affordance that no sibling fields are around to share.
//   'plain' - borderless, transparent with a custom chevron. Use when
//             sibling fields share the affordance pattern (e.g. the profile
//             Details grid, where many floating-label rows together signal
//             "this is editable"). The transparent surface inherits the
//             parent's material so the control doesn't punch through.

interface SelectProps {
  value: string | null | undefined;
  onChange: (next: string) => void;
  options: readonly FieldOption[];
  placeholder?: string;
  className?: string;
  editable?: boolean;
  variant?: "box" | "plain";
  disabled?: boolean;
  // Suppresses the synthetic empty option. Use when the value is guaranteed
  // to match one of the supplied options (no "unselected" state).
  hideEmpty?: boolean;
  // Keeps the synthetic empty option as a placeholder but makes it
  // unselectable: the field starts empty, yet once a real option is chosen
  // there is no way back to "nothing".
  required?: boolean;
  // Optional DOM id set on the <select> — a stable, locale-independent hook
  // for e2e tests (spark-validation).
  id?: string;
}

export function Select({
  value,
  onChange,
  options,
  placeholder = "–",
  className = "",
  editable = true,
  variant = "box",
  disabled = false,
  hideEmpty = false,
  required = false,
  id,
}: SelectProps) {
  if (!editable) {
    const label = options.find((o) => o.value === value)?.label;
    return (
      <span className={`field-value text-ink ${className}`}>
        {label ? label : <span className="text-ink/30">{placeholder}</span>}
      </span>
    );
  }

  const base =
    variant === "plain"
      ? `field-value w-full min-w-0 cursor-pointer appearance-none bg-transparent pr-5 rounded focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:opacity-50 ${
          value ? "font-medium text-ink/80" : "text-ink/30"
        }`
      : // box variant: native chevron hidden via appearance-none; the chevron
        // is drawn as a background SVG (see chevronStyle below) and pr-8
        // reserves room so it sits a comfortable distance from the rounded
        // border on every browser.
        "field-value w-full min-w-0 cursor-pointer appearance-none rounded bg-surface pl-3 pr-8 py-2.5 text-ink ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:opacity-50";

  const select = (
    <select
      id={id}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`${base} ${className}`}
      style={variant === "box" ? CHEVRON_STYLE : undefined}
    >
      {hideEmpty ? null : (
        <option value="" disabled={required}>
          {placeholder}
        </option>
      )}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );

  if (variant === "plain") {
    return (
      <span className="relative block w-full">
        {select}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="pointer-events-none absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 text-ink/40"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
    );
  }

  return select;
}

// Chevron rendered as a CSS background-image so it lives on the <select>
// itself — no wrapping element, which keeps `flex-1` / `w-32` consumer classes
// working untouched. Stroke ink/40 matches the plain variant's SVG chevron.
const CHEVRON_STYLE = {
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='rgba(17,17,17,0.4)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>\")",
  backgroundPosition: "right 0.75rem center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "0.75rem",
} as const;
